import { useState, useCallback, useEffect, useRef } from 'react';
import useFetch, { muteAll } from 'hooks/useFetch';
import { createCardViewList } from 'utils';
import * as debugConfig from '../debug';
import { AssetFilterCategory } from '../types';
import { getStockGroup } from '../actions';
const INITIAL_STATE = {
    loading: true,
    error: false,
    data: null,
    count: 0,
};
const useStockGroups = (id, type, selectedFilter) => {
    const [state, setState] = useState(INITIAL_STATE);
    const currentPage = useRef(0);
    const countRef = useRef(0);
    const symbolsSet = useRef(undefined);
    const dataRef = useRef(null);
    const currentFilterRef = useRef(AssetFilterCategory.NAME);
    const isStillMounted = useRef(true);
    const [, , , fetch] = useFetch(muteAll);
    const reset = useCallback(() => {
        setState((state) => ({ ...INITIAL_STATE, count: state.count }));
    }, []);
    const search = useCallback(async (selectedFilter) => {
        if (selectedFilter) {
            currentFilterRef.current = selectedFilter;
        }
        const filterDidChange = !!selectedFilter;
        const currentFilter = currentFilterRef.current;
        if (!filterDidChange) {
            // fetch next page
            currentPage.current += 1;
        }
        else {
            /**
             * Filter changed or started paginating so fetch again
             */
            currentPage.current = 1;
            dataRef.current = [];
            symbolsSet.current = undefined;
        }
        const response = await fetch(getStockGroup(id, type, currentPage.current, currentFilterRef.current));
        /**
         * Checking for filter changed.
         * If the filter has changed, prevent the old api to populate
         */
        if (isStillMounted.current && currentFilterRef.current === currentFilter) {
            const responseAssets = response?.assets;
            if (responseAssets) {
                if (!symbolsSet.current || !dataRef.current) {
                    dataRef.current = [];
                    symbolsSet.current = new Set();
                }
                for (let i = 0; i < responseAssets.length; i += 1) {
                    const { symbol } = responseAssets[i];
                    if (symbol) {
                        if (!symbolsSet.current.has(symbol)) {
                            const { icon, ...asset } = responseAssets[i];
                            if (icon) {
                                symbolsSet.current.add(symbol);
                                dataRef.current.push({ ...asset, iconUrl: icon });
                            }
                        }
                    }
                }
                countRef.current = response.paging.count;
                setState({
                    loading: false,
                    error: false,
                    count: response.paging.count,
                    data: createCardViewList(dataRef.current),
                });
                if (response.assets.length > 0 && debugConfig.LOAD_NEXT_PAGE_RIGHT_AWAY) {
                    search(); // fetch next page right away
                }
            }
            else {
                setState({ loading: false, error: true, data: createCardViewList(dataRef.current), count: countRef.current });
            }
        }
    }, [id, type]);
    useEffect(() => {
        search(selectedFilter);
    }, [search, selectedFilter]);
    useEffect(() => {
        reset();
    }, [reset, selectedFilter]);
    useEffect(() => {
        isStillMounted.current = true;
        return () => {
            isStillMounted.current = false;
        };
    }, []);
    return [state.data, search, state.loading, state.count];
};
export default useStockGroups;
